import { useEffect, useState } from "react";

/**
 * Keeps the screen on while `enabled` (Screen Wake Lock API). The lock is released by the
 * browser whenever the page is hidden, so it is requested again when the page comes back.
 * Returns whether a lock is currently held.
 */
export default function useWakeLock(enabled) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const wakeLock = typeof navigator !== "undefined" ? navigator.wakeLock : undefined;
    // (the previous run's cleanup has already reset `active`)
    if (!enabled || !wakeLock) return undefined;

    let sentinel = null;
    let disposed = false;

    const request = async () => {
      if (disposed || document.visibilityState !== "visible") return;
      try {
        sentinel = await wakeLock.request("screen");
        setActive(true);
        sentinel.addEventListener("release", () => {
          sentinel = null;
          setActive(false);
        });
      } catch {
        // denied (low battery, unsupported): the screen simply behaves as usual
        setActive(false);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && !sentinel) request();
    };

    request();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (sentinel) sentinel.release().catch(() => {});
      setActive(false);
    };
  }, [enabled]);

  return active;
}
